import { BillingTier, PaymentSchedule, PlanType } from '@/types'
import { calculateTrialDays } from '@/utils/dates'

export const PLAN_REF: Record<PlanType, Record<PaymentSchedule, string>> = {
  [PlanType.Pro]: {
    [PaymentSchedule.Monthly]: 'FLOW_PRO_MONTHLY',
    [PaymentSchedule.Annual]: 'FLOW_PRO_YEARLY',
  },
  [PlanType.Team]: {
    // Map Team to Pro plans for new checkouts - existing Team subscriptions are grandfathered
    [PaymentSchedule.Monthly]: 'FLOW_PRO_MONTHLY',
    [PaymentSchedule.Annual]: 'FLOW_PRO_YEARLY',
  },
  [PlanType.Enterprise]: {
    [PaymentSchedule.Monthly]: 'FLOW_BUSINESS_MONTHLY',
    [PaymentSchedule.Annual]: 'FLOW_BUSINESS_YEARLY',
  },
}

/**
 * Both the Pro and Team (legacy) plans can be upgraded to the Enterprise plan.
 * This function checks if the current plan is one of these lower tier plans.
 */
export const isLowerTierPlan = (currentPlanType: PlanType | null) => {
  if (!currentPlanType) return false
  return currentPlanType === PlanType.Pro || currentPlanType === PlanType.Team
}

// Display label only — the PlanType enum value must stay as-is, portal analytics ride it.
export const getPaidPlanLabel = (planType: PlanType | null, tier: BillingTier | undefined): string | null => {
  if (!planType) return null
  if (planType !== PlanType.Enterprise) return planType
  if (tier === 'business') return 'Growth'
  return PlanType.Enterprise
}

export const getPlanTypeFromProductName = (productName: string): PlanType | null => {
  if (['FLOW_PRO_MONTHLY', 'FLOW_PRO_YEARLY', 'FLOW_PRO_NO_BILLING'].includes(productName)) {
    return PlanType.Pro
  }

  if (['FLOW_BUSINESS_MONTHLY', 'FLOW_BUSINESS_YEARLY'].includes(productName)) {
    return PlanType.Enterprise
  }

  if (['FLOW_TEAM_MONTHLY', 'FLOW_TEAM_YEARLY'].includes(productName)) {
    return PlanType.Team
  }

  return null
}

export const isMemberOnTrial = (trialEndsAt: number | null) => {
  if (!trialEndsAt) return false
  return trialEndsAt * 1000 > Date.now()
}

// "In grace" = `grace_period_ends_at` is in the future AND we're past any active trial. Backend resets `trial_ends_at` to 0 the moment the trial ends, while keeping `grace_period_ends_at` live for the window.
export const isInGracePeriod = (
  trialEndsAt: number | null | undefined,
  gracePeriodEndsAt: number | null | undefined
): boolean => {
  if (!gracePeriodEndsAt) return false
  const now = Date.now()
  if (gracePeriodEndsAt * 1000 <= now) return false
  if (trialEndsAt && trialEndsAt * 1000 > now) return false
  return true
}

// Offer the "Rejoin your team" screen only when the server says the most-recent
// enterprise is actually rejoinable (still active, no live billing). The raw
// `most_recent_enterprise_id` pointer persists after a team is disbanded, so keying
// the screen on its presence trapped removed/disbanded users on a dead-end. When the
// server omits the flag (older backend mid-rollout), fall back to pointer presence so
// rejoin doesn't regress for those users.
export const canRejoinEnterprise = (
  subscription: {
    most_recent_enterprise_rejoinable?: boolean
    most_recent_enterprise_id?: string | null
  } | null
): boolean => {
  return subscription?.most_recent_enterprise_rejoinable ?? Boolean(subscription?.most_recent_enterprise_id)
}

// Whole days left in grace, rounded up so "<24h" reads as "1 day". Delegates to `calculateTrialDays` to match the trial-countdown rounding shown elsewhere.
export const gracePeriodDaysLeft = (
  trialEndsAt: number | null | undefined,
  gracePeriodEndsAt: number | null | undefined
): number => {
  if (!isInGracePeriod(trialEndsAt, gracePeriodEndsAt) || !gracePeriodEndsAt) return 0
  return calculateTrialDays(gracePeriodEndsAt)
}
